import { useEffect, useRef } from "react";

/**
 * Keeps the two halves of the comparator on the same place.
 *
 * A comparator is only useful if both sides show the same cell, so one scroll
 * drives the other. The guard stops the mirrored scroll echoing back.
 */
export const useMirroredScroll = (settled: unknown, visible = true) => {
  const currentRef = useRef<HTMLDivElement>(null);
  const proposedRef = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);
  // `scroll` does not bubble, so each scroll area's own viewport gets the
  // listener rather than a React prop further up the tree.
  useEffect(() => {
    const left = currentRef.current;
    const right = proposedRef.current;
    if (!visible || !left || !right) return;
    const mirror = (source: HTMLDivElement, target: HTMLDivElement) => () => {
      if (syncing.current) return;
      syncing.current = true;
      target.scrollTop = source.scrollTop;
      target.scrollLeft = source.scrollLeft;
      requestAnimationFrame(() => {
        syncing.current = false;
      });
    };
    const onLeft = mirror(left, right);
    const onRight = mirror(right, left);
    left.addEventListener("scroll", onLeft);
    right.addEventListener("scroll", onRight);
    return () => {
      left.removeEventListener("scroll", onLeft);
      right.removeEventListener("scroll", onRight);
    };
  }, [settled, visible]);

  return { currentRef, proposedRef };
};
